import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useParams } from 'react-router-dom';

import { FormFieldsData, FormShowTemplate } from 'framework/form';
import { Alert, Loading } from 'framework/ui/mui-builder';
import { ShowPageLayout } from 'framework/ui/layout';
import { EmployeeDisplayDataProvider } from 'domain/shared/employee';
import { ProjectTeamType, useProjectTeamService } from 'domain/shared/newProject';
import { StaffingDataType, StaffingProjectData } from 'domain/shared/staffing';
import { mapTeamStructureWithAssignments } from 'domain/shared/assignment';
import { GET_STAFFING_PROJECT_CONFIRMATION_DATA } from 'domain/shared/assignment/graphql/assignmentsQueries';
import ProjectTeamPositionsList from './ProjectTeamPositionsList';

type QueryResult = {
  baseProject: StaffingDataType;
};

type TeamPositionsProjectData = StaffingProjectData & {
  legacyId?: number;
  team?: StaffingDataType['team'];
  name?: string;
};

const ProjectTeamPositionsLayout = () => {
  const { id } = useParams<string>();
  const [reloadKey, setReloadKey] = useState(0);
  const { createProjectTeam, updateProjectTeam, getProjectTeamValidationSchema } = useProjectTeamService();

  const { data, loading, error, refetch } = useQuery<QueryResult>(GET_STAFFING_PROJECT_CONFIRMATION_DATA, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const projectData = (data?.baseProject || null) as TeamPositionsProjectData | null;

  if (!id) {
    return <Alert severity="error">`id` route param is required.</Alert>;
  }

  if (loading && !projectData) {
    return <Loading isInitialPosition />;
  }

  if (error || !projectData) {
    return <Alert severity="error">Failed to load project team positions.</Alert>;
  }

  const initialData = mapTeamStructureWithAssignments(projectData, projectData.team as ProjectTeamType);
  const employeeAliases = Array.from(
    new Set(
      initialData.team.expectedStructure.flatMap(slot => (slot.members || []).map(member => member.employeeAlias)),
    ),
  ).filter(Boolean);

  const submitFormFunc = async (formData: FormFieldsData) => {
    const requestBody = {
      id: formData.team.id,
      projectId: projectData.id,
      location: formData.team.location,
      type: formData.team.type,
      expectedStructure: formData.team.expectedStructure,
    };

    const submitFunc = formData.team?.id ? updateProjectTeam : createProjectTeam;
    return submitFunc(requestBody);
  };

  return (
    <FormShowTemplate
      key={reloadKey}
      initialData={initialData}
      getValidationSchemaFunc={getProjectTeamValidationSchema}
      submitFormFunc={submitFormFunc}
      afterSubmit={{
        message: 'Team positions have been successfully updated.',
        execute: async () => {
          await refetch();
          setReloadKey(prev => prev + 1);
        },
      }}
    >
      <EmployeeDisplayDataProvider aliases={employeeAliases}>
        <ShowPageLayout>
          <ProjectTeamPositionsList />
        </ShowPageLayout>
      </EmployeeDisplayDataProvider>
    </FormShowTemplate>
  );
};

export default ProjectTeamPositionsLayout;
